// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Tag from "@insite/mobius/Icons/Tag";
import X from "@insite/mobius/Icons/X";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { wishListCardStyles } from "@synd/Overrides/Components/WishListCard";
import Favorite from "@synd/XDL/Icons/Favorite";
import LinkSignOutBase from "@synd/XDL/Icons/LinkSignOutBase";
import Unfavorite from "@synd/XDL/Icons/Unfavorite";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, getSystemColor, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

wishListCardStyles.cardContainer = {
    css: css`
        display: grid;
        grid-template-columns: 52px 1fr;
        grid-template-rows: min(17px);
        column-gap: 12px;
        row-gap: 4px;
        padding: 4px;
        align-items: start;
        &:hover {
            background: ${color("common.ui8")};
            cursor: pointer;
        }
    `,
};

wishListCardStyles.title = {
    ...fontConfig.xSmallSingleLine,
    weight: fontConfig.weight.medium,
    color: "common.ui7",
};

wishListCardStyles.miniProductImage = {
    css: css`
        grid-row: 1 / span 2;
        max-width: 52px;
    `,
};

wishListCardStyles.miniLastUpdatedText = {
    ...fontConfig.xSmallSingleLine,
};

wishListCardStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: minmax(200px, 1fr) 317px minmax(70px, 91px) minmax(150px, 204px);
        grid-column-gap: 22px;
        grid-row-gap: 12px;
        justify-content: space-between;
        width: 100%;

        ${xScreen("lte", "md")} {
            grid-template-columns: 1fr;
        }
    `,
};

// Description Section Styles
wishListCardStyles.descriptionWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,
};

wishListCardStyles.nameWrapper = {
    css: css`
        display: flex;
        & > ${StyledButton} {
            font-weight: ${fontConfig.weight.bold};
            ${TypographyStyle} {
                font-size: ${fontConfig.regular.size}px;
                line-height: 22px;
            }
        }
    `,
};

wishListCardStyles.lastUpdatedText = {
    size: fontConfig.xSmall.size,
    lineHeight: fontConfig.xSmall.lineHeight,
};

wishListCardStyles.descriptionText = {
    size: fontConfig.xSmall.size,
    lineHeight: fontConfig.xSmall.lineHeight,
    css: css`
        ${ellipsis(3)}
    `,
};

wishListCardStyles.actionFavoriteClickable = {
    css: css`
        width: auto;
        font-size: ${fontConfig.xSmall.size}px;
        line-height: ${fontConfig.xSmall.lineHeight};
        font-weight: ${fontConfig.weight.bold};
        gap: 6px;
        color: ${getSystemColor("text.link")};
        &:hover {
            &&&,
            ${IconWrapper} {
                color: ${getSystemColor("common.ui10")};
            }
        }
    `,
};

wishListCardStyles.favTagsWrapper = {
    css: css`
        display: flex;
        flex-direction: row;
        column-gap: 24px;
        &:empty {
            display: none;
        }
    `,
};

wishListCardStyles.starIcon = {
    color: "text.link",
    src: Favorite,
    size: 14,
};

wishListCardStyles.starXIcon = {
    color: "text.link",
    src: Unfavorite,
    size: 14,
};

wishListCardStyles.tagTooltip = {
    mergeCss: true,
    cssOverrides: {
        tooltipClickable: css`
            display: flex;
            align-items: center;
            font-size: ${fontConfig.xSmall.size}px;
            line-height: ${fontConfig.xSmall.lineHeight};
            margin: 0;
            gap: 6px;
        `,
    },
};

wishListCardStyles.tagIcon = {
    color: "text.link",
    src: Tag,
    size: 14,
};

wishListCardStyles.imagesWrapper = {
    css: css`
        display: flex;
        gap: 12px;
    `,
};

wishListCardStyles.productImageLink = {
    css: css`
        display: flex;
        align-items: flex-start;
        margin: 0;
        height: 52px;
        aspect-ratio: 1/1;
        > ${TypographyStyle} {
            width: 100%;
        }
    `,
};

wishListCardStyles.productImage = {
    css: css`
        &,
        img {
            aspect-ratio: 1/1;
        }

        img {
            object-fit: contain;
        }
    `,
};

wishListCardStyles.sharingStatusWrapper = {
    css: css`
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
        ${xScreen("lte", "md")} {
            flex-direction: row;
            justify-content: space-between;
        }
    `,
};

wishListCardStyles.actionAddToCartButton = {
    css: css`
        white-space: nowrap;
        width: 100%;
        padding: 0 10px;
    `,
};

wishListCardStyles.additionalActions = {
    css: css`
        display: flex;
        justify-content: end;
        margin-top: 12px;
        flex-direction: column;
        align-items: end;
        gap: 6px;
        ${xScreen("lte", "md")} {
            flex-direction: row;
            justify-content: space-between;
        }
    `,
};
wishListCardStyles.actionDeleteLink = {
    ...createLinkIconStyles({
        icon: X,
        sizeVariant: "xsmall",
        linkStyles: {
            css: css`
                ${IconWrapper} {
                    &,
                    & > svg {
                        width: 11px;
                    }
                }
            `,
        },
    }),
};

wishListCardStyles.itemsSmallHeading = {
    wrapper: {
        css: css`
            flex-direction: row;
            width: auto;
        `,
    },
    heading: {
        css: css`
            font-size: ${fontConfig.xSmall.size}px;
            line-height: ${fontConfig.xSmall.lineHeight};
            font-weight: ${fontConfig.weight.regular};
            text-transform: capitalize;
            margin-right: 4px;
            &::after {
                content: ":";
            }
        `,
    },
    text: {
        css: css`
            font-size: ${fontConfig.xSmall.size}px;
            line-height: ${fontConfig.xSmall.lineHeight};
            font-weight: ${fontConfig.weight.bold};
            width: auto;
        `,
    },
};

wishListCardStyles.actionHideLink = {
    ...createLinkIconStyles({
        icon: "Eye",
        sizeVariant: "xsmall",
        linkStyles: {
            css: css`
                ${IconWrapper} {
                    &,
                    & > svg {
                        width: 11px;
                    }
                }
            `,
        },
    }),
};

wishListCardStyles.actionLeaveLink = {
    ...createLinkIconStyles({
        icon: LinkSignOutBase,
        sizeVariant: "xsmall",
        linkStyles: {
            css: css`
                ${IconWrapper} {
                    &,
                    & > svg {
                        width: 11px;
                    }
                }
            `,
        },
    }),
};
